'use client'

/**
 * PriceChart Component
 *
 * A responsive chart component that displays bond price and yield data with
 * configurable time ranges and display modes.
 *
 * Component Hierarchy:
 * PriceChart
 * ├── ChartContainer
 * │   ├── SwitchContainer
 * │   │   ├── DisplayModeSwitch
 * │   │   │   └── Switch (display mode: yield/price)
 * │   │   └── TimeRangeSwitch
 * │   │       └── Switch (time ranges: 1D, 1W, 1M, 3M, 6M, 1Y, All)
 * │   └── LineChart
 * │       └── ChartTooltip
 *
 * Helper Functions:
 * ├── getTimeRangeParams: Generates date range and granularity based on selected time range
 * ├── formatXAxisDate: Formats dates for X-axis labels based on time range
 * ├── formatTooltipDate: Formats dates for tooltip display
 * ├── formatTooltipTime: Formats time for tooltip display
 * ├── getIntervalByTimeRange: Returns the number of data points to display based on time range
 *
 * @example
 * <PriceChart
 *   isin="US1234567890"
 *   width="100%"
 *   height={400}
 *   showGrid={true}
 * />
 */

import type { LineChartProps } from '@/components/atomic/line-chart/types'
import type { PriceData } from '@/types/api/prices'

import { eachWeekendOfInterval } from 'date-fns/eachWeekendOfInterval'
import { useEffect, useMemo, useState } from 'react'

import { ChartTooltip } from '@/components/atomic/chart-tooltip'
import LineChart from '@/components/atomic/line-chart'
import Switch from '@/components/atomic/switch'
import { useGetPricesQuery } from '@/legacy-api/data-slice'
import { formatYield } from '@/utils/presentation/bonds'
import { formatPrice } from '@/utils/presentation/formatting'

import {
  displayModeOptions,
  formatTooltipDate,
  formatTooltipTime,
  formatXAxisDate,
  getIntervalByTimeRange,
  getTimeRangeParams,
  timeRangeOptions,
} from './helpers'
import {
  ChartContainer,
  DisplayModeSwitch,
  SwitchContainer,
  TimeRangeSwitch,
} from './styles'
import {
  DisplayMode,
  type DisplayModeType,
  TimeRange,
  type TimeRangeType,
} from './types'

interface PriceChartProps {
  width?: number | string
  height?: number | string
  isin: string
  showGrid?: boolean
  freezeDataDelay?: number
  // Add testData prop for Storybook/testing
  testData?: PriceData[]
}

function PriceChart({
  width,
  height,
  isin,
  showGrid = true,
  freezeDataDelay = 10,
  testData, // for storybook testing
}: PriceChartProps) {
  const [displayMode, setDisplayMode] = useState<DisplayModeType>(
    DisplayMode.YIELD,
  )
  const [timeRange, setTimeRange] = useState<TimeRangeType>(TimeRange.ONE_MONTH)
  const [useFastAnimation, setUseFastAnimation] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    setUseFastAnimation(false)
  }, [isin])

  // Get params for all time ranges
  const oneDayParams = getTimeRangeParams(TimeRange.ONE_DAY)
  const oneWeekParams = getTimeRangeParams(TimeRange.ONE_WEEK)
  const oneMonthParams = getTimeRangeParams(TimeRange.ONE_MONTH)
  const threeMonthParams = getTimeRangeParams(TimeRange.THREE_MONTHS)
  const sixMonthParams = getTimeRangeParams(TimeRange.SIX_MONTHS)
  const oneYearParams = getTimeRangeParams(TimeRange.ONE_YEAR)
  const allParams = getTimeRangeParams(TimeRange.ALL)

  // Current time range params - only need start/end for weekends calculation
  const { start, end } = getTimeRangeParams(timeRange)

  // Skip API calls if testData is provided
  const skipQuery = testData !== undefined

  // Fetch all time ranges in parallel (unless testData is provided)
  const oneDay = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: oneDayParams.granularity,
      start: oneDayParams.start,
      end: oneDayParams.end,
    },
    { skip: skipQuery },
  )

  const oneWeek = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: oneWeekParams.granularity,
      start: oneWeekParams.start,
      end: oneWeekParams.end,
    },
    { skip: skipQuery },
  )

  const oneMonth = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: oneMonthParams.granularity,
      start: oneMonthParams.start,
      end: oneMonthParams.end,
    },
    { skip: skipQuery },
  )

  const threeMonths = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: threeMonthParams.granularity,
      start: threeMonthParams.start,
      end: threeMonthParams.end,
    },
    { skip: skipQuery },
  )

  const sixMonths = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: sixMonthParams.granularity,
      start: sixMonthParams.start,
      end: sixMonthParams.end,
    },
    { skip: skipQuery },
  )

  const oneYear = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: oneYearParams.granularity,
      start: oneYearParams.start,
      end: oneYearParams.end,
    },
    { skip: skipQuery },
  )

  const all = useGetPricesQuery(
    {
      instrumentID: isin,
      frequency: allParams.granularity,
      start: allParams.start,
      end: allParams.end,
    },
    { skip: skipQuery },
  )

  // Get the current active query based on selected time range
  const activeQuery = useMemo(
    () =>
      ({
        [TimeRange.ONE_DAY]: oneDay,
        [TimeRange.ONE_WEEK]: oneWeek,
        [TimeRange.ONE_MONTH]: oneMonth,
        [TimeRange.THREE_MONTHS]: threeMonths,
        [TimeRange.SIX_MONTHS]: sixMonths,
        [TimeRange.ONE_YEAR]: oneYear,
        [TimeRange.ALL]: all,
      })[timeRange] || oneMonth,
    [
      timeRange,
      oneDay,
      oneWeek,
      oneMonth,
      threeMonths,
      sixMonths,
      oneYear,
      all,
    ],
  )

  // Use testData if provided, otherwise use API data
  const prices = useMemo(() => {
    const apiData = activeQuery.data?.data || []

    return {
      data: testData || apiData,
    }
  }, [testData, activeQuery.data])

  const isPricesLoading = !testData && activeQuery.isLoading
  const isFetching = !testData && activeQuery.isFetching

  const weekendsInInterval = useMemo(() => {
    return new Set(
      eachWeekendOfInterval({
        start: new Date(start),
        end: new Date(end),
      }).map((date) => date.toDateString()),
    )
  }, [start, end])

  const formattedData: LineChartProps['data'] = useMemo(() => {
    return (
      prices?.data
        .filter(
          (point) =>
            !weekendsInInterval.has(new Date(point.timestamp).toDateString()),
        )
        .map((point) => {
          const date = new Date(point.timestamp)

          return {
            x: formatXAxisDate(date, timeRange),
            tooltipDate: formatTooltipDate(date, timeRange),
            tooltipTime: formatTooltipTime(date, timeRange),
            y:
              displayMode === DisplayMode.PRICE
                ? point.price
                : point.yield_to_worst,
            price: point.price,
            yield_to_worst: point.yield_to_worst,
          }
        }) || []
    )
  }, [prices, displayMode, timeRange, weekendsInInterval])

  const tooltipFormatter = useMemo(() => {
    const TooltipComponent = (dataPoint: LineChartProps['data'][0]) => {
      const tooltipValues =
        displayMode === DisplayMode.PRICE
          ? [
              {
                label: 'Price',
                value: formatPrice(dataPoint.price, {}, 3),
                color: 'var(--color-moment-teal-600)',
              },
              {
                label: 'YTW',
                value: formatYield(dataPoint.yield_to_worst),
              },
            ]
          : [
              {
                label: 'YTW',
                value: formatYield(dataPoint.yield_to_worst),
                color: 'var(--color-moment-teal-600)',
              },
              {
                label: 'Price',
                value: formatPrice(dataPoint.price, {}, 3),
              },
            ]

      return (
        <ChartTooltip
          className="w-48"
          date={dataPoint.tooltipDate}
          time={dataPoint.tooltipTime}
          values={tooltipValues}
        />
      )
    }
    TooltipComponent.displayName = 'PriceChartTooltip'
    return TooltipComponent
  }, [displayMode])

  // Handle display mode change
  const handleDisplayModeChange = (value: DisplayModeType) => {
    setDisplayMode(value)
    setUseFastAnimation(true)
  }

  // Handle time range change
  const handleTimeRangeChange = (value: TimeRangeType) => {
    setTimeRange(value)
    setUseFastAnimation(true)
  }

  return (
    <ChartContainer>
      <SwitchContainer>
        <DisplayModeSwitch>
          <Switch
            value={displayMode}
            options={displayModeOptions}
            onChange={handleDisplayModeChange}
          />
        </DisplayModeSwitch>
        <TimeRangeSwitch>
          <Switch
            value={timeRange}
            options={timeRangeOptions}
            onChange={handleTimeRangeChange}
          />
        </TimeRangeSwitch>
      </SwitchContainer>

      <LineChart
        data={formattedData}
        width={width}
        height={height}
        tooltipFormatter={tooltipFormatter}
        showGrid={showGrid}
        displayMode={displayMode}
        interval={getIntervalByTimeRange(timeRange)}
        isPricesLoading={isPricesLoading}
        isFetching={isFetching}
        useFastAnimation={useFastAnimation}
        onAnimationEnd={() => setUseFastAnimation(true)}
        freezeDataDelay={freezeDataDelay}
      />
    </ChartContainer>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default PriceChart
